import React, { useState, Component } from 'react';
import { Home, AlertCircle, Package, Settings, X, Building2, ClipboardList, MapPin, FileText, Users, CheckCircle, Wallet, Contact, ChevronDown, Wrench, Calendar, Globe } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { PropertySelector } from './PropertySelector';
interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}
interface NavChild {
  label: string;
  path: string;
}
interface NavItem {
  icon: ComponentType<{
    className?: string;
  }>;
  label: string;
  path: string;
  // When present, the item is a collapsible group. `path` acts as the
  // base path used to detect the active/expanded state.
  children?: NavChild[];
}
export function Sidebar({
  isOpen = true,
  onClose
}: SidebarProps) {
  const location = useLocation();
  const isCm = location.pathname.startsWith('/cm');
  const isMan = location.pathname.startsWith('/man');
  // Host navigation
  const hostNav: NavItem[] = [{
    icon: Home,
    label: 'Dashboard',
    path: '/'
  }, {
    icon: Calendar,
    label: 'Pianificazione',
    path: '/pianificazione'
  }, {
    icon: Wrench,
    label: 'Richiedi intervento',
    path: '/richiedi-intervento'
  }, {
    icon: ClipboardList,
    label: 'Storico',
    path: '/storico'
  }, {
    icon: AlertCircle,
    label: 'Segnalazioni',
    path: '/segnalazioni'
  }];
  // Manutentore navigation
  const manNav: NavItem[] = [{
    icon: Home,
    label: 'Dashboard',
    path: '/man'
  }, {
    icon: MapPin,
    label: 'Opportunità',
    path: '/man/opportunita'
  }, {
    icon: ClipboardList,
    label: 'Incarichi',
    path: '/man/incarichi'
  }, {
    icon: FileText,
    label: 'Proposte',
    path: '/man/proposte'
  }];
  const manSecondary: NavItem[] = [{
    icon: CheckCircle,
    label: 'Qualità',
    path: '/man/qualita'
  }, {
    icon: Wallet,
    label: 'Guadagni',
    path: '/man/guadagni'
  }, {
    icon: Globe,
    label: 'Pagina pubblica',
    path: '/man/pagina-pubblica'
  }, {
    icon: Settings,
    label: 'Impostazioni',
    path: '/man/impostazioni'
  }];
  const hostSecondary: NavItem[] = [{
    icon: Building2,
    label: 'Proprietà',
    path: '/scheda-proprieta'
  }, {
    icon: Package,
    label: 'Pacchetti pulizie',
    path: '/pacchetti'
  }, {
    icon: Settings,
    label: 'Impostazioni',
    path: '/impostazioni'
  }];
  // Cleaner Manager navigation
  const cmNav: NavItem[] = [{
    icon: Home,
    label: 'Dashboard',
    path: '/cm'
  }, {
    icon: MapPin,
    label: 'Opportunità',
    path: '/cm/opportunita'
  }, {
    icon: ClipboardList,
    label: 'Incarichi',
    path: '/cm/incarichi'
  }, {
    icon: FileText,
    label: 'Proposte',
    path: '/cm/proposte'
  }, {
    icon: Contact,
    label: 'Clienti',
    path: '/cm/clienti',
    children: [{
      label: 'Referenti',
      path: '/cm/clienti/referenti'
    }, {
      label: 'Appartamenti',
      path: '/cm/clienti/appartamenti'
    }]
  }];
  const cmSecondary: NavItem[] = [{
    icon: Users,
    label: 'Team',
    path: '/cm/team'
  }, {
    icon: CheckCircle,
    label: 'Qualità',
    path: '/cm/qualita'
  }, {
    icon: Wallet,
    label: 'Guadagni',
    path: '/cm/guadagni'
  }, {
    icon: Globe,
    label: 'Pagina pubblica',
    path: '/cm/pagina-pubblica'
  }, {
    icon: Settings,
    label: 'Impostazioni',
    path: '/cm/impostazioni'
  }];
  const navItems = isCm ? cmNav : isMan ? manNav : hostNav;
  const secondaryItems = isCm ? cmSecondary : isMan ? manSecondary : hostSecondary;
  // Tracks which collapsible groups are manually open. When undefined for a
  // group, it falls back to auto-expanding if the current route is inside it.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const toggleGroup = (path: string) => {
    setOpenGroups(prev => {
      const current = prev[path] ?? location.pathname.startsWith(path);
      return {
        ...prev,
        [path]: !current
      };
    });
  };
  const handleLinkClick = () => {
    if (onClose) onClose();
  };
  const isActivePath = (itemPath: string) => {
    if (isCm) {
      // Dashboard root must match exactly so it doesn't stay active everywhere
      if (itemPath === '/cm') return location.pathname === '/cm';
      return location.pathname === itemPath;
    }
    if (isMan) {
      if (itemPath === '/man') return location.pathname === '/man';
      // Keep "Incarichi" highlighted on the detail page too
      if (itemPath === '/man/incarichi') return location.pathname.startsWith('/man/incarichi');
      return location.pathname === itemPath;
    }
    // Host: "Pulizie" stays highlighted on the detail page too
    if (itemPath === '/pulizie') {
      return location.pathname === '/pulizie' || location.pathname === '/pulizia-dettaglio';
    }
    return itemPath === '/' ? location.pathname === '/' || location.pathname === '/dashboard' : location.pathname === itemPath;
  };
  return <>
      {/* Mobile Overlay */}
      {isOpen && onClose && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} data-id="element-1077" />}

      {/* Sidebar */}
      <aside className={`
          bg-[#0D1B6F] h-screen fixed left-0 top-0 flex flex-col z-50
          w-64 lg:translate-x-0
          transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `} data-id="element-1078">
        {/* Logo Header */}
        <div className="h-16 px-4 border-b border-white/10 flex items-center justify-between flex-shrink-0" data-id="element-1079">
          <span className="text-2xl font-bold tracking-tight select-none" data-id="element-1080">
            <span className="text-white" data-id="element-1081">Clean</span>
            <span className="text-teal-deep" data-id="element-1082">AI</span>
          </span>
          {onClose && <button onClick={onClose} className="lg:hidden p-2 hover:bg-white/10 rounded-full transition-colors" data-id="element-1083">
              <X size={20} className="text-white" data-id="element-1084" />
            </button>}
        </div>

        {/* Context: Host property selector only (CM / Manutentore have no zone context) */}
        {!isCm && !isMan && <PropertySelector isCollapsed={false} data-id="element-1085" />}

        {/* Navigation */}
        <nav className="flex-1 px-3 py-2 overflow-y-auto" data-id="element-1086">
          <div className="mb-1" data-id="element-1087">
            <p className="px-3 pt-3 pb-2 text-[11px] font-semibold text-white/40 uppercase tracking-widest" data-id="element-1088">
              Menu
            </p>
            <ul className="space-y-0.5" data-id="element-1089">
              {navItems.map(item => {
              const Icon = item.icon;
              // Collapsible group (e.g. Clienti → Referenti / Appartamenti)
              if (item.children) {
                const groupActive = location.pathname.startsWith(item.path);
                const expanded = openGroups[item.path] ?? groupActive;
                return <li key={item.label} data-id="element-1090">
                      <button type="button" onClick={() => toggleGroup(item.path)} aria-expanded={expanded} className={`
                          w-full flex items-center gap-3 rounded-xl px-3 py-2.5
                          transition-all duration-150 min-h-[42px]
                          ${groupActive ? 'text-white font-medium' : 'text-white/60 hover:bg-white/8 hover:text-white/90'}
                        `} data-id="element-1091">
                        <Icon className={`w-[18px] h-[18px] flex-shrink-0 ${groupActive ? 'text-white' : ''}`} data-id="element-1092" />
                        <span className="text-[14px] flex-1 text-left" data-id="element-1093">
                          {item.label}
                        </span>
                        <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} data-id="element-1094" />
                      </button>
                      {expanded && <ul className="mt-0.5 ml-4 pl-3 border-l border-white/10 space-y-0.5" data-id="element-1095">
                          {item.children.map(child => {
                      const childActive = location.pathname === child.path;
                      return <li key={child.path} data-id="element-1096">
                                <Link to={child.path} onClick={handleLinkClick} className={`
                                    w-full flex items-center rounded-lg px-3 py-2
                                    transition-all duration-150 min-h-[38px] text-[13px]
                                    ${childActive ? 'bg-coral text-white font-medium' : 'text-white/55 hover:bg-white/8 hover:text-white/90'}
                                  `} data-id="element-1097">
                                  {child.label}
                                </Link>
                              </li>;
                    })}
                        </ul>}
                    </li>;
              }
              const isActive = isActivePath(item.path);
              return <li key={item.label} data-id="element-1098">
                    <Link to={item.path} onClick={handleLinkClick} className={`
                        w-full flex items-center gap-3 rounded-xl px-3 py-2.5
                        transition-all duration-150 min-h-[42px]
                        ${isActive ? 'bg-coral text-white font-medium' : 'text-white/60 hover:bg-white/8 hover:text-white/90'}
                      `} data-id="element-1099">
                      <Icon className={`w-[18px] h-[18px] flex-shrink-0 ${isActive ? 'text-white' : ''}`} data-id="element-1100" />
                      <span className="text-[14px]" data-id="element-1101">{item.label}</span>
                    </Link>
                  </li>;
            })}
            </ul>
          </div>

          <div className="mt-4" data-id="element-1102">
            <p className="px-3 pt-3 pb-2 text-[11px] font-semibold text-white/40 uppercase tracking-widest" data-id="element-1103">
              Gestione
            </p>
            <ul className="space-y-0.5" data-id="element-1104">
              {secondaryItems.map(item => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return <li key={item.label} data-id="element-1105">
                    <Link to={item.path} onClick={handleLinkClick} className={`
                        w-full flex items-center gap-3 rounded-xl px-3 py-2.5
                        transition-all duration-150 min-h-[42px]
                        ${isActive ? 'bg-coral text-white font-medium' : 'text-white/60 hover:bg-white/8 hover:text-white/90'}
                      `} data-id="element-1106">
                      <Icon className={`w-[18px] h-[18px] flex-shrink-0 ${isActive ? 'text-white' : ''}`} data-id="element-1107" />
                      <span className="text-[14px]" data-id="element-1108">{item.label}</span>
                    </Link>
                  </li>;
            })}
            </ul>
          </div>
        </nav>
      </aside>
    </>;
}